import { create } from "zustand";
import { persist } from "zustand/middleware";

// ---------------------------------------------------------------------------
// Field definitions — single source of truth for types, defaults & options
// ---------------------------------------------------------------------------

export type StylesConfigFieldDef =
  | { type: "boolean"; defaultValue: boolean; tooltip?: string }
  | { type: "enum"; defaultValue: string; options: readonly string[]; tooltip?: string }
  | {
      type: "number";
      defaultValue: number;
      min?: number;
      max?: number;
      step?: number;
      tooltip?: string;
    };

export const STYLES_CONFIG_FIELDS = {
  showChatInput: { type: "boolean", defaultValue: true },
  scrollSnap: { type: "boolean", defaultValue: false },
  stepHeight: { type: "enum", defaultValue: "screen", options: ["normal", "screen"] },
  traceMode: {
    type: "enum",
    defaultValue: "plan",
    options: ["deliberate", "plan", "disabled"],
    tooltip:
      "Trace/insight rendering mode. `deliberate` = full trace. `plan` = plan-trace cards only (default). `disabled` = nuclear switch: no trace/insight UI + server strips the insight prompt layer and `signal` tool availability. Applies to new sessions — mid-session toggles do not retroactively change an active session.",
  },
  stageTransition: {
    type: "enum",
    defaultValue: "rich-card",
    options: ["disabled", "rich-card"],
  },
  loadingVariant: {
    type: "enum",
    defaultValue: "spinner",
    options: ["disabled", "dots", "texts", "spinner", "text-single"],
    tooltip:
      "`dots` = streaming dots. `texts` = morphing star + rotating warm copy. `spinner` = morphing star only (no text). `text-single` = morphing star + static 'Thinking...'. `disabled` = no loading indicator.",
  },
  loadingDelayMs: {
    type: "number",
    defaultValue: 1500,
    min: 0,
    max: 10000,
    step: 250,
  },
  profileJourneyIcon: {
    type: "enum",
    defaultValue: "hide",
    options: ["show", "hide"],
  },
  authMode: {
    type: "enum",
    defaultValue: "dismissible",
    options: ["dismissible", "blocking"],
    tooltip:
      "Auth gate behavior at the variant's auth checkpoint. `blocking` = `show_auth` cannot be cancelled (Cancel hidden, modal close suppressed); server rejects forged dismissals. `dismissible` (default) = Cancel button visible; dismissal advances graph state per `authRequiredBefore`.",
  },
} as const satisfies Record<string, StylesConfigFieldDef>;

// ---------------------------------------------------------------------------
// Derived types
// ---------------------------------------------------------------------------

export type StylesConfig = {
  -readonly [K in keyof typeof STYLES_CONFIG_FIELDS]: (typeof STYLES_CONFIG_FIELDS)[K] extends {
    type: "boolean";
  }
    ? boolean
    : (typeof STYLES_CONFIG_FIELDS)[K] extends { type: "number" }
      ? number
      : (typeof STYLES_CONFIG_FIELDS)[K] extends { options: readonly (infer O)[] }
        ? O
        : string;
};

export type StylesConfigKey = keyof StylesConfig;

export type VariantOverridesInput = {
  overrides: Partial<StylesConfig>;
};

// ---------------------------------------------------------------------------
// Defaults (derived from field defs)
// ---------------------------------------------------------------------------

export const STYLES_CONFIG_DEFAULTS: StylesConfig = Object.fromEntries(
  Object.entries(STYLES_CONFIG_FIELDS).map(([k, v]) => [k, v.defaultValue]),
) as StylesConfig;

// ---------------------------------------------------------------------------
// Cascade resolver
// ---------------------------------------------------------------------------

function resolveConfig(
  defaults: StylesConfig,
  variantOverrides: Partial<StylesConfig>,
  userPrefs: Partial<StylesConfig>,
): StylesConfig {
  const resolved = { ...defaults };
  for (const key of Object.keys(defaults) as StylesConfigKey[]) {
    const variantValue = variantOverrides[key];
    if (variantValue !== undefined) {
      (resolved as Record<string, unknown>)[key] = variantValue;
    }
    const userValue = userPrefs[key];
    if (userValue !== undefined) {
      (resolved as Record<string, unknown>)[key] = userValue;
    }
  }
  return resolved;
}

// ---------------------------------------------------------------------------
// Pre-computed field entries (avoids repeated cast at call sites)
// ---------------------------------------------------------------------------

export const STYLES_CONFIG_FIELD_ENTRIES = Object.entries(STYLES_CONFIG_FIELDS) as [
  StylesConfigKey,
  StylesConfigFieldDef,
][];

// ---------------------------------------------------------------------------
// Cascade source resolver (pure — usable in UI badges, tests, etc.)
// ---------------------------------------------------------------------------

export function resolveSource(
  key: StylesConfigKey,
  variantOverrides: Partial<StylesConfig>,
  userPrefs: Partial<StylesConfig>,
): "default" | "variant" | "user" {
  if (userPrefs[key] !== undefined) return "user";
  if (variantOverrides[key] !== undefined) return "variant";
  return "default";
}

// ---------------------------------------------------------------------------
// Data-attribute sync
// ---------------------------------------------------------------------------

export function toKebab(key: string): string {
  return key.replace(/([A-Z])/g, "-$1").toLowerCase();
}

export function syncDataAttributes(config: StylesConfig): void {
  if (typeof document === "undefined") return;
  const el = document.documentElement;
  for (const [key, value] of Object.entries(config)) {
    el.setAttribute(`data-config-${toKebab(key)}`, String(value));
  }
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export type StylesConfigStore = {
  variantOverrides: Partial<StylesConfig>;
  userPrefs: Partial<StylesConfig>;

  setVariantOverrides: (input: VariantOverridesInput) => void;
  setUserPref: <K extends StylesConfigKey>(key: K, value: StylesConfig[K]) => void;
  resetUserPrefs: () => void;
  getResolved: () => StylesConfig;
};

const STORAGE_KEY = "styles-config-user-prefs";

export const useStylesConfig = create<StylesConfigStore>()(
  persist(
    (set, get) => ({
      variantOverrides: {},
      userPrefs: {},

      setVariantOverrides: (input) => set({ variantOverrides: input.overrides }),

      setUserPref: (key, value) => {
        set((state) => ({ userPrefs: { ...state.userPrefs, [key]: value } }));
      },

      resetUserPrefs: () => set({ userPrefs: {} }),

      getResolved: () => {
        const { variantOverrides, userPrefs } = get();
        return resolveConfig(STYLES_CONFIG_DEFAULTS, variantOverrides, userPrefs);
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({ userPrefs: state.userPrefs }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          syncDataAttributes(state.getResolved());
        }
      },
    },
  ),
);

// ---------------------------------------------------------------------------
// Selector hook — subscribe to a single resolved value
// ---------------------------------------------------------------------------

export function useStylesConfigValue<K extends StylesConfigKey>(key: K): StylesConfig[K] {
  return useStylesConfig((state) => {
    const { variantOverrides, userPrefs } = state;
    const userValue = userPrefs[key];
    if (userValue !== undefined) return userValue;
    const variantValue = variantOverrides[key];
    if (variantValue !== undefined) return variantValue;
    return STYLES_CONFIG_DEFAULTS[key];
  });
}
